/**
 * Mirrors backend idea_status_transitions for stage-card CTAs.
 */

export const PIPELINE_STAGES = [
  { id: 'submitted', label: 'Submitted' },
  { id: 'under_review', label: 'Under review' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'optimization', label: 'Optimization' },
  { id: 'escalated_to_it_bizops', label: 'IT BizOps' },
  { id: 'it_review', label: 'IT review' },
  { id: 'deploying', label: 'Deploying' },
  { id: 'published', label: 'Published' },
];

const STATUS_ALIASES = {
  'under-review': 'under_review',
  'in-progress': 'in_progress',
  'needs-info': 'needs_info',
  'it-review': 'it_review',
  'routed-to-it': 'it_review',
};

const FORWARD = {
  submitted: ['under_review'],
  under_review: ['in_progress'],
  in_progress: ['optimization'],
  optimization: ['escalated_to_it_bizops', 'deploying'],
  escalated_to_it_bizops: [],
  it_review: ['deploying'],
  deploying: [],
  needs_info: ['under_review', 'in_progress'],
  on_hold: ['under_review', 'in_progress'],
  rejected: ['under_review'],
  published: [],
};

const ROUTE_TO_IT_FROM = new Set([
  'submitted',
  'under_review',
  'in_progress',
  'optimization',
  'escalated_to_it_bizops',
  'needs_info',
  'on_hold',
  'it_review',
  'deploying',
]);

const ESCALATE_BIZOPS_FROM = new Set([
  'submitted',
  'under_review',
  'in_progress',
  'optimization',
  'needs_info',
  'on_hold',
  'escalated_to_it_bizops',
  'it_review',
  'deploying',
]);

export function normalizeStatus(status) {
  if (!status) return '';
  return STATUS_ALIASES[status] || status;
}

export function stageLabel(id) {
  return PIPELINE_STAGES.find((s) => s.id === id)?.label || id;
}

export function isSalesPersona(request) {
  return (request?.primaryPersona || '') === 'Sales';
}

/** Side branches — entering these must not complete skipped main-path stages. */
export const SIDE_BRANCH_STAGES = new Set([
  'it_review',
  'escalated_to_it_bizops',
]);

const PAUSED_STATUSES = new Set(['needs_info', 'on_hold', 'rejected']);

/** Pipeline stages shown on the rail (BizOps only for Sales). */
export function visiblePipelineStages(request) {
  const sales = isSalesPersona(request);
  const current = normalizeStatus(request?.status);
  return PIPELINE_STAGES.filter((s) => {
    if (s.id !== 'escalated_to_it_bizops') return true;
    return sales || current === 'escalated_to_it_bizops';
  });
}

/**
 * Visit-based rail states. Never paints "everything before current index" green.
 *
 * - With activity (detail page): visited = stages seen in the feed + current.
 * - Without activity (list cards): trust API workflowTimeline (backend is visit-based).
 * - Fallback: only Submitted completed, current active.
 */
export function resolveWorkflowRail(request, { activity = [], stages } = {}) {
  const rail = stages || visiblePipelineStages(request);
  const current = normalizeStatus(request?.status);
  const apiTimeline = Array.isArray(request?.workflowTimeline)
    ? request.workflowTimeline
    : [];
  const byId = Object.fromEntries(apiTimeline.map((s) => [s.id, s]));
  const hasActivity = Array.isArray(activity) && activity.length > 0;

  if (!hasActivity && apiTimeline.length > 0) {
    return rail.map((stage) => {
      const api = byId[stage.id];
      return {
        id: stage.id,
        label: stage.label || api?.label || stage.id,
        state: api?.state || 'pending',
        reviewerData: api?.reviewerData || null,
      };
    });
  }

  const visited = new Set(['submitted']);
  (activity || []).forEach((e) => {
    const to = normalizeStatus(e.toStage || e.to_stage);
    if (to && !PAUSED_STATUSES.has(to)) visited.add(to);
  });
  if (current && !PAUSED_STATUSES.has(current)) {
    visited.add(current);
  }

  let activeId = current;
  if (PAUSED_STATUSES.has(current)) {
    let furthest = 'submitted';
    rail.forEach((s) => {
      if (visited.has(s.id)) furthest = s.id;
    });
    activeId = furthest;
  } else if (!rail.some((s) => s.id === current)) {
    activeId = 'submitted';
  }

  return rail.map((stage) => {
    let state = 'pending';
    if (stage.id === activeId) state = 'active';
    else if (visited.has(stage.id)) state = 'completed';

    const passedItReview =
      stage.id === 'it_review' &&
      state === 'pending' &&
      (current === 'deploying' || current === 'published') &&
      Boolean(
        request?.itSubflowNode ||
          request?.itScansNotApplicable ||
          request?.secScanRepoId ||
          request?.securityJobId
      );
    if (passedItReview) state = 'completed';

    const api = byId[stage.id];
    return {
      id: stage.id,
      label: stage.label || api?.label || stage.id,
      state,
      reviewerData: state !== 'pending' ? api?.reviewerData || null : null,
    };
  });
}

function pipelineIndex(status) {
  const n = normalizeStatus(status);
  if (n === 'needs_info' || n === 'on_hold' || n === 'rejected') {
    return PIPELINE_STAGES.findIndex((s) => s.id === 'under_review');
  }
  return PIPELINE_STAGES.findIndex((s) => s.id === n);
}

export function earlierPipelineStages(fromStatus, request) {
  const n = normalizeStatus(fromStatus);
  if (n === 'published') return [];
  const idx = pipelineIndex(n);
  if (idx <= 0) return [];
  const sales = isSalesPersona(request);
  return PIPELINE_STAGES.slice(0, idx).filter((s) => {
    if (s.id !== 'escalated_to_it_bizops') return true;
    return sales;
  });
}

export function canRouteToIT(status) {
  const n = normalizeStatus(status);
  return ROUTE_TO_IT_FROM.has(n) && n !== 'rejected' && n !== 'published';
}

export function canEscalateBizOps(status, request) {
  if (!isSalesPersona(request)) return false;
  const n = normalizeStatus(status);
  return ESCALATE_BIZOPS_FROM.has(n) && n !== 'rejected' && n !== 'published';
}

export function isAlreadyRoutedToIT(status) {
  const n = normalizeStatus(status);
  return n === 'it_review';
}

export function canPublishFromStatus(status, request, itReviewPayload) {
  const n = normalizeStatus(status);
  if (n !== 'deploying') return false;
  if (request?.itScansNotApplicable) return true;
  const itLinked = Boolean(request?.secScanRepoId || request?.securityJobId);
  if (!itLinked) return true;
  const it = itReviewPayload?.itReview || itReviewPayload;
  if (it?.itScansNotApplicable) return true;
  if (it?.gatesSatisfied === true || it?.gates_satisfied === true) return true;
  if (it?.scansPassed && it?.developmentComplete) return true;
  return false;
}

export function isGemRequest(request) {
  return String(request?.appType || '').toLowerCase() === 'gem';
}

/**
 * Primary forward target(s) for the active status (excluding send-back / side).
 */
export function forwardTargets(fromStatus, request) {
  const n = normalizeStatus(fromStatus);
  // BizOps is an independent side action (Sales only), not a forward hop.
  let targets = [...(FORWARD[n] || [])].filter((t) => t !== 'escalated_to_it_bizops');
  if (n === 'optimization' && (request?.secScanRepoId || request?.itSubflowNode)) {
    return targets.filter((t) => t !== 'deploying');
  }
  return targets;
}

export function canReject(status) {
  const n = normalizeStatus(status);
  return n && n !== 'published' && n !== 'rejected';
}

export function canHold(status) {
  const n = normalizeStatus(status);
  return ['submitted', 'under_review', 'in_progress', 'optimization', 'escalated_to_it_bizops'].includes(
    n
  );
}

export function canRequestInfo(status) {
  const n = normalizeStatus(status);
  return [
    'submitted',
    'under_review',
    'in_progress',
    'optimization',
    'escalated_to_it_bizops',
    'on_hold',
  ].includes(n);
}

export const FORWARD_LABELS = {
  under_review: 'Move to Under review',
  in_progress: 'Approve → In progress',
  optimization: 'Advance to Optimization',
  escalated_to_it_bizops: 'Escalate to IT BizOps',
  deploying: 'Move to Deploying',
  published: 'Mark published',
};
